// src/cockpit4/components/CustomerArchivePanel.tsx
// ============================================
// 客户档案面板 - 客户分类 + 档案跟进备案
//
// 功能：
//   1. 按分类筛选客户（全部/酒店/AI/潜在/跟踪/已成交/其他）
//   2. 按跟进状态二级筛选（全部/线索/谈判中/已成交/已流失/活跃）—— 行业×阶段双重分类
//   3. 跟进提醒快捷按钮（30天未联系 / 快成交）
//   4. KPI 摘要卡片（总客户数 / 30天未联系 / 快成交 / 总预估金额）
//   5. 客户列表展示（公司/分类/等级/负责人/下次联系/预估金额）
//   6. 点击客户查看完整档案（历史沟通/成交记录/AI分析/下一步建议）
//   7. 数据来源：CRMRuntime 查询（真实 Obsidian Vault 数据，无数据时显示"暂无客户"）
//      白皮书铁律一：Obsidian 是唯一大脑，禁止 mock 假数据
// ============================================

import { useEffect, useState, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users,
  Building2,
  Brain,
  UserPlus,
  Crosshair,
  CheckCircle2,
  FolderOpen,
  X,
  Phone,
  Mail,
  MessageSquare,
  MapPin,
  Calendar,
  DollarSign,
  TrendingUp,
  RefreshCw,
  Loader2,
  FileText,
  AlertTriangle,
  Flame,
  Wallet,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import {
  type CustomerProfile,
  type CustomerCategory,
  type CustomerStatus,
  type CRMStats,
  CUSTOMER_CATEGORY_CONFIG,
} from "../../runtime/CRMRuntime/types";

// 分类筛选配置
const CATEGORY_FILTERS: Array<{ key: CustomerCategory | "all"; label: string; icon: typeof Users; color: string }> = [
  { key: "all", label: "全部", icon: Users, color: "#9CA3AF" },
  { key: "hotel", label: "酒店", icon: Building2, color: "#FBBF24" },
  { key: "ai", label: "AI", icon: Brain, color: "#38BDF8" },
  { key: "potential", label: "潜在", icon: UserPlus, color: "#A78BFA" },
  { key: "tracking", label: "跟踪", icon: Crosshair, color: "#FB923C" },
  { key: "active", label: "已成交", icon: CheckCircle2, color: "#34D399" },
  { key: "other", label: "其他", icon: FolderOpen, color: "#6B7280" },
];

// 跟进状态筛选配置（行业×阶段双重分类中的"阶段"维度）
const STATUS_FILTERS: Array<{ key: CustomerStatus | "all"; label: string; color: string }> = [
  { key: "all", label: "全部状态", color: "#9CA3AF" },
  { key: "prospecting", label: "线索", color: "#60A5FA" },
  { key: "negotiating", label: "谈判中", color: "#FBBF24" },
  { key: "closed-won", label: "已成交", color: "#34D399" },
  { key: "closed-lost", label: "已流失", color: "#F87171" },
  { key: "active", label: "活跃", color: "#A78BFA" },
];

// 等级颜色映射
const TIER_COLOR_MAP: Record<string, string> = {
  "S+": "#A855F7",
  S: "#EC4899",
  A: "#3B82F6",
  B: "#6B7280",
  C: "#4B5563",
};

export default function CustomerArchivePanel() {
  const [customers, setCustomers] = useState<CustomerProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeCategory, setActiveCategory] = useState<CustomerCategory | "all">("all");
  // 行业×阶段双重分类：阶段维度（默认 "all"）
  const [activeStatus, setActiveStatus] = useState<CustomerStatus | "all">("all");
  // 跟进提醒：是否仅看 30 天未联系客户
  const [showStaleOnly, setShowStaleOnly] = useState(false);
  // KPI 摘要卡片数据（来自 CRM stats 查询，失败时为 null，UI 显示 "--"）
  const [stats, setStats] = useState<CRMStats | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerProfile | null>(null);

  // 加载 CRM 统计概览（白皮书铁律一：查询失败不显示假数据）
  const loadStats = useCallback(async () => {
    try {
      if (!RuntimeManager.isInitialized()) return;
      const resp = await RuntimeManager.query(RuntimeName.CRM, { type: "stats" });
      if (resp?.success && resp.data) {
        const inner = resp.data as any;
        // CRMRuntime 返回 { success, data: CRMStats, timestamp }
        const s = inner?.data ?? inner;
        if (s && typeof s === "object") {
          setStats(s as CRMStats);
        }
      }
    } catch {
      // 降级：保持 null，KPI 卡片显示 "--"
    }
  }, []);

  // 等待 RuntimeManager 初始化并加载客户列表
  const loadCustomers = useCallback(async () => {
    setRefreshing(true);
    try {
      // 轮询等待 RuntimeManager 就绪
      for (let i = 0; i < 30; i++) {
        if (RuntimeManager.isInitialized()) break;
        await new Promise((r) => setTimeout(r, 300));
      }
      if (!RuntimeManager.isInitialized()) {
        setLoading(false);
        return;
      }
      const resp = await RuntimeManager.query(RuntimeName.CRM, { type: "list" });
      if (resp?.success && resp.data) {
        const inner = resp.data as any;
        // CRMRuntime 返回 { success, data: CustomerProfile[], timestamp }
        const list = inner?.data || inner || [];
        setCustomers(Array.isArray(list) ? list : []);
      }
      // 同步刷新 KPI 摘要（独立查询，失败不影响列表）
      loadStats();
    } catch {
      // 降级
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [loadStats]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  // 行业×阶段二维交叉过滤（c.category === activeCategory && c.followup.status === activeStatus）
  // "all" 表示该维度不限制；showStaleOnly 叠加 30 天未联系过滤
  const filteredCustomers = useMemo(() => {
    return customers.filter((c) => {
      if (activeCategory !== "all" && c.category !== activeCategory) return false;
      if (activeStatus !== "all" && c.followup.status !== activeStatus) return false;
      if (showStaleOnly) {
        const last = new Date(c.followup.lastContact);
        if (isNaN(last.getTime())) return false;
        const diffDays = Math.floor((Date.now() - last.getTime()) / (24 * 60 * 60 * 1000));
        if (diffDays < 30) return false;
      }
      return true;
    });
  }, [customers, activeCategory, activeStatus, showStaleOnly]);

  // 分类计数
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { all: customers.length };
    for (const c of customers) {
      counts[c.category] = (counts[c.category] || 0) + 1;
    }
    return counts;
  }, [customers]);

  // 状态计数（阶段维度）
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: customers.length };
    for (const c of customers) {
      counts[c.followup.status] = (counts[c.followup.status] || 0) + 1;
    }
    return counts;
  }, [customers]);

  // 30 天未联系客户数（本地计算，用于跟进提醒按钮徽章）
  const staleCountLocal = useMemo(() => {
    const now = Date.now();
    return customers.filter((c) => {
      const last = new Date(c.followup.lastContact);
      if (isNaN(last.getTime())) return false;
      return Math.floor((now - last.getTime()) / (24 * 60 * 60 * 1000)) >= 30;
    }).length;
  }, [customers]);

  // 快成交客户数（status === "negotiating"，用于跟进提醒按钮徽章）
  const closingCountLocal = useMemo(
    () => customers.filter((c) => c.followup.status === "negotiating").length,
    [customers]
  );

  // P12 短板5：线索来源归因分布（基于真实 Vault 客户档案 source 字段实时统计）
  // 归因维度：公众号/抖音/知乎/GEO基线/官网/转介绍/其他，用于内容与活动归因复盘
  const sourceDistribution = useMemo(() => {
    const map: Record<string, number> = {};
    for (const c of customers) {
      const s = c.source || "未归因";
      map[s] = (map[s] || 0) + 1;
    }
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);
  }, [customers]);

  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
      {/* ============ 头部：标题 + 刷新 ============ */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FolderOpen size={14} className="text-amber-400" />
          <span className="text-[12px] font-mono text-amber-300 uppercase tracking-wider">
            客户档案与跟进备案
          </span>
          <span className="text-[10px] font-mono text-gray-600">
            ({customers.length} 个客户)
          </span>
        </div>
        <button
          onClick={loadCustomers}
          disabled={refreshing}
          className="p-1 text-gray-500 hover:text-amber-400 transition-colors"
          title="刷新客户列表"
        >
          <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
        </button>
      </div>

      {/* ============ KPI 摘要卡片（数据来自 CRM stats 查询，失败显示 "--"）============ */}
      <div className="grid grid-cols-4 gap-2 mb-3">
        <KpiCard
          icon={Users}
          label="总客户数"
          value={stats ? String(stats.total) : "--"}
          color="#9CA3AF"
        />
        <KpiCard
          icon={AlertTriangle}
          label="30天未联系"
          value={stats ? String(stats.staleCount) : "--"}
          color="#F87171"
        />
        <KpiCard
          icon={Flame}
          label="快成交"
          value={stats ? String(stats.closingCount) : "--"}
          color="#FBBF24"
        />
        <KpiCard
          icon={Wallet}
          label="总预估金额"
          value={
            stats
              ? stats.totalEstimatedValue > 0
                ? `¥${(stats.totalEstimatedValue / 10000).toFixed(1)}w`
                : "¥0"
              : "--"
          }
          color="#34D399"
        />
      </div>

      {/* ============ 线索来源归因（P12 短板5：内容/活动/渠道归因，基于真实 Vault source 字段）============ */}
      <div className="mb-3 rounded-md border border-white/[0.06] bg-white/[0.02] px-3 py-2">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
            线索来源归因
          </span>
          <Crosshair size={10} className="text-cyan-400" />
        </div>
        <div className="flex gap-1 h-1.5">
          {sourceDistribution.length === 0 ? (
            <span className="text-[9px] font-mono text-gray-600">暂无来源归因数据</span>
          ) : (
            sourceDistribution.map(([s, n]) => {
              const pct = customers.length
                ? Math.round((n / customers.length) * 100)
                : 0;
              return (
                <div
                  key={s}
                  className="h-full rounded-full bg-cyan-500/60"
                  style={{ width: pct === 0 ? 1 : Math.max(pct, 3) + "%" }}
                  title={`${s}：${n} 个（${pct}%）`}
                />
              );
            })
          )}
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
          {sourceDistribution.length === 0 ? null : (
            sourceDistribution.map(([s, n]) => {
              const pct = customers.length
                ? Math.round((n / customers.length) * 100)
                : 0;
              return (
                <span key={s} className="text-[9px] font-mono text-gray-400">
                  <span className="text-cyan-300">{s}</span> {n}
                  <span className="text-gray-600">（{pct}%）</span>
                </span>
              );
            })
          )}
        </div>
      </div>

      {/* ============ 分类筛选 Tab（行业维度）============ */}
      <div className="flex items-center gap-1 mb-2 flex-wrap">
        {CATEGORY_FILTERS.map((f) => {
          const isActive = activeCategory === f.key;
          const count = categoryCounts[f.key] || 0;
          return (
            <button
              key={f.key}
              onClick={() => setActiveCategory(f.key)}
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-mono transition-all ${
                isActive
                  ? "bg-white/[0.08] border border-white/10"
                  : "bg-transparent border border-transparent hover:bg-white/[0.03]"
              }`}
              style={isActive ? { color: f.color } : { color: "#6B7280" }}
            >
              <f.icon size={10} style={{ color: f.color }} />
              <span>{f.label}</span>
              <span className="text-[9px] opacity-60">({count})</span>
            </button>
          );
        })}
      </div>

      {/* ============ 状态筛选 Tab（阶段维度，行业×阶段双重分类）============ */}
      <div className="flex items-center gap-1 mb-2 flex-wrap">
        {STATUS_FILTERS.map((f) => {
          const isActive = activeStatus === f.key;
          const count = statusCounts[f.key] || 0;
          return (
            <button
              key={f.key}
              onClick={() => setActiveStatus(f.key)}
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-mono transition-all ${
                isActive
                  ? "bg-white/[0.08] border border-white/10"
                  : "bg-transparent border border-transparent hover:bg-white/[0.03]"
              }`}
              style={isActive ? { color: f.color } : { color: "#6B7280" }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: f.color }}
              />
              <span>{f.label}</span>
              <span className="text-[9px] opacity-60">({count})</span>
            </button>
          );
        })}
      </div>

      {/* ============ 跟进提醒快捷按钮区 ============ */}
      <div className="flex items-center gap-2 mb-3">
        <button
          onClick={() => setShowStaleOnly((v) => !v)}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-mono transition-all border ${
            showStaleOnly
              ? "bg-red-500/15 border-red-500/40 text-red-300"
              : "bg-transparent border-white/[0.06] text-gray-400 hover:bg-white/[0.03]"
          }`}
          title="筛选出 30 天未联系的客户"
        >
          <AlertTriangle size={11} className={showStaleOnly ? "text-red-400" : "text-red-500/70"} />
          <span>30天未联系</span>
          <span
            className={`ml-0.5 px-1 rounded text-[9px] font-bold ${
              showStaleOnly ? "bg-red-500/30 text-red-200" : "bg-white/[0.06] text-gray-300"
            }`}
          >
            {staleCountLocal}
          </span>
        </button>
        <button
          onClick={() =>
            setActiveStatus((cur) => (cur === "negotiating" ? "all" : "negotiating"))
          }
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-mono transition-all border ${
            activeStatus === "negotiating"
              ? "bg-amber-500/15 border-amber-500/40 text-amber-300"
              : "bg-transparent border-white/[0.06] text-gray-400 hover:bg-white/[0.03]"
          }`}
          title="筛选出谈判中（快成交）的客户"
        >
          <Flame size={11} className={activeStatus === "negotiating" ? "text-amber-400" : "text-amber-500/70"} />
          <span>快成交</span>
          <span
            className={`ml-0.5 px-1 rounded text-[9px] font-bold ${
              activeStatus === "negotiating" ? "bg-amber-500/30 text-amber-200" : "bg-white/[0.06] text-gray-300"
            }`}
          >
            {closingCountLocal}
          </span>
        </button>
        {(showStaleOnly || activeStatus !== "all") && (
          <button
            onClick={() => {
              setShowStaleOnly(false);
              setActiveStatus("all");
            }}
            className="px-2 py-1 rounded-md text-[10px] font-mono text-gray-500 hover:text-gray-300 transition-colors"
            title="清除提醒筛选"
          >
            清除
          </button>
        )}
      </div>

      {/* ============ 客户列表 ============ */}
      {loading ? (
        <div className="py-6 text-center">
          <Loader2 size={16} className="mx-auto text-amber-400 animate-spin" />
          <p className="text-[10px] font-mono text-gray-600 mt-1">加载客户档案...</p>
        </div>
      ) : filteredCustomers.length === 0 ? (
        <div className="py-6 text-center">
          <p className="text-[11px] text-gray-600">
            暂无
            {activeCategory === "all" ? "" : CATEGORY_FILTERS.find(f => f.key === activeCategory)?.label}
            {activeStatus !== "all" ? `/${STATUS_FILTERS.find(f => f.key === activeStatus)?.label}` : ""}
            {showStaleOnly ? "/30天未联系" : ""}
            客户
          </p>
        </div>
      ) : (
        <div className="space-y-1 max-h-[400px] overflow-y-auto pr-1">
          {/* 表头 */}
          <div className="grid grid-cols-12 gap-2 px-2 py-1 text-[9px] font-mono text-gray-600 uppercase tracking-wider border-b border-white/[0.06] sticky top-0 bg-[#0a0e1a]/80 backdrop-blur-sm">
            <span className="col-span-3">公司</span>
            <span className="col-span-2">分类</span>
            <span className="col-span-1">等级</span>
            <span className="col-span-2">负责人</span>
            <span className="col-span-2">下次联系</span>
            <span className="col-span-2 text-right">预估金额</span>
          </div>
          {/* 行 */}
          {filteredCustomers.map((c, i) => {
            const catConfig = CUSTOMER_CATEGORY_CONFIG[c.category] || CUSTOMER_CATEGORY_CONFIG.other;
            return (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, x: -4 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.02 }}
                onClick={() => setSelectedCustomer(c)}
                className="grid grid-cols-12 gap-2 px-2 py-1.5 items-center text-[10px] font-mono border-b border-white/[0.04] last:border-b-0 hover:bg-white/[0.03] rounded cursor-pointer transition-colors"
              >
                <div className="col-span-3 flex items-center gap-1.5 min-w-0">
                  <span className="text-gray-600 w-3 text-center">{i + 1}</span>
                  <span className="text-gray-200 truncate">{c.company}</span>
                </div>
                <div className="col-span-2 flex items-center gap-1">
                  <span
                    className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{ background: catConfig.color }}
                  />
                  <span style={{ color: catConfig.color }}>{catConfig.label}</span>
                </div>
                <span className="col-span-1">
                  <span
                    className="px-1 py-0.5 rounded text-[9px] font-bold"
                    style={{
                      color: TIER_COLOR_MAP[c.tier] || "#6B7280",
                      background: `${TIER_COLOR_MAP[c.tier] || "#6B7280"}20`,
                    }}
                  >
                    {c.tier}
                  </span>
                </span>
                <span className="col-span-2 text-gray-400 truncate">{c.owner}</span>
                <span className="col-span-2 text-gray-400">
                  {c.followup.nextContact || "—"}
                </span>
                <span className="col-span-2 text-right text-emerald-400 font-semibold">
                  {c.followup.estimatedValue > 0
                    ? `¥${(c.followup.estimatedValue / 10000).toFixed(1)}w`
                    : "—"}
                </span>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* ============ 客户档案详情 Modal ============ */}
      <AnimatePresence>
        {selectedCustomer && (
          <CustomerDetailModal
            customer={selectedCustomer}
            onClose={() => setSelectedCustomer(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================
// KPI 摘要小卡片（总客户数 / 30天未联系 / 快成交 / 总预估金额）
// ============================================
interface KpiCardProps {
  icon: typeof Users;
  label: string;
  value: string;
  color: string;
}

function KpiCard({ icon: Icon, label, value, color }: KpiCardProps) {
  return (
    <div
      className="rounded-md border border-white/[0.06] bg-white/[0.02] px-2 py-1.5 flex items-center gap-1.5"
      style={{ borderLeft: `2px solid ${color}` }}
    >
      <Icon size={12} style={{ color }} className="flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider truncate">
          {label}
        </div>
        <div className="text-[12px] font-mono font-semibold truncate" style={{ color }}>
          {value}
        </div>
      </div>
    </div>
  );
}

// ============================================
// 客户档案详情 Modal
// ============================================
interface DetailModalProps {
  customer: CustomerProfile;
  onClose: () => void;
}

function CustomerDetailModal({ customer, onClose }: DetailModalProps) {
  const catConfig = CUSTOMER_CATEGORY_CONFIG[customer.category] || CUSTOMER_CATEGORY_CONFIG.other;
  const sections = [
    { key: "history", label: "历史沟通", icon: MessageSquare, color: "#3B82F6", content: customer.history },
    { key: "deals", label: "成交记录", icon: DollarSign, color: "#10B981", content: customer.deals },
    { key: "analysis", label: "AI 分析", icon: Brain, color: "#A855F7", content: customer.analysis },
    { key: "suggestion", label: "下一步建议", icon: TrendingUp, color: "#F59E0B", content: customer.suggestion },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
        className="w-[800px] max-h-[85vh] rounded-xl border border-white/10 bg-[#0a0e1a]/95 backdrop-blur-xl overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-white/[0.06]">
          <div className="flex items-start gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: `${catConfig.color}20`, border: `1px solid ${catConfig.color}40` }}
            >
              <Building2 size={18} style={{ color: catConfig.color }} />
            </div>
            <div>
              <h3 className="text-[16px] font-semibold text-white">{customer.company}</h3>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span
                  className="px-1.5 py-0.5 rounded text-[10px] font-mono"
                  style={{ background: `${catConfig.color}20`, color: catConfig.color }}
                >
                  {catConfig.label}
                </span>
                <span
                  className="px-1.5 py-0.5 rounded text-[10px] font-mono font-bold"
                  style={{
                    color: TIER_COLOR_MAP[customer.tier] || "#6B7280",
                    background: `${TIER_COLOR_MAP[customer.tier] || "#6B7280"}20`,
                  }}
                >
                  {customer.tier}
                </span>
                <span className="text-[10px] font-mono text-gray-500">{customer.industry}</span>
                <span className="flex items-center gap-0.5 text-[10px] font-mono text-gray-500">
                  <MapPin size={9} /> {customer.region}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-white transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* 联系人信息 */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
              <div className="text-[10px] font-mono text-gray-500 uppercase mb-2">联系人信息</div>
              <div className="space-y-1.5 text-[11px] font-mono">
                <div className="flex items-center gap-2 text-gray-300">
                  <Users size={11} className="text-gray-500" />
                  {customer.contact.name}
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <Phone size={11} className="text-gray-500" />
                  {customer.contact.phone || "—"}
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <MessageSquare size={11} className="text-gray-500" />
                  {customer.contact.wechat || "—"}
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <Mail size={11} className="text-gray-500" />
                  {customer.contact.email || "—"}
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
              <div className="text-[10px] font-mono text-gray-500 uppercase mb-2">跟进信息</div>
              <div className="space-y-1.5 text-[11px] font-mono">
                <div className="flex items-center gap-2 text-gray-400">
                  <Calendar size={11} className="text-gray-500" />
                  上次联系：{customer.followup.lastContact || "—"}
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <Calendar size={11} className="text-gray-500" />
                  下次联系：{customer.followup.nextContact || "—"}
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <DollarSign size={11} className="text-gray-500" />
                  预估金额：
                  <span className="text-emerald-400 font-semibold">
                    {customer.followup.estimatedValue > 0
                      ? `¥${customer.followup.estimatedValue.toLocaleString()}`
                      : "—"}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <Users size={11} className="text-gray-500" />
                  负责人：{customer.owner}
                </div>
                {/* P12 短板5：线索来源归因展示 */}
                <div className="flex items-center gap-2 text-gray-400">
                  <Crosshair size={11} className="text-cyan-500" />
                  线索来源：{customer.source || "未归因"}
                </div>
              </div>
            </div>
          </div>

          {/* 标签 */}
          {customer.tags.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              {customer.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-blue-500/10 text-blue-400 border border-blue-500/20"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* 档案章节 */}
          {sections.map((s) => (
            <div key={s.key} className="rounded-lg border border-white/[0.06] bg-white/[0.01] p-3">
              <div className="flex items-center gap-2 mb-2">
                <s.icon size={12} style={{ color: s.color }} />
                <span className="text-[11px] font-mono uppercase tracking-wider" style={{ color: s.color }}>
                  {s.label}
                </span>
              </div>
              <div className="text-[11px] text-gray-300 leading-relaxed whitespace-pre-wrap">
                {s.content || "暂无记录"}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-white/[0.06] flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-[10px] font-mono text-gray-600">
            <FileText size={10} />
            <span>档案路径：{customer.filePath}</span>
          </div>
          <button
            onClick={onClose}
            className="px-3 py-1 rounded-md text-[11px] font-mono text-gray-300 bg-white/[0.05] hover:bg-white/[0.1] transition-colors"
          >
            关闭
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
